import {
  CRON_RUN_STATUSES,
  type CronRunStatus,
  type SchedulerJobItem,
  type SchedulerRunItem,
} from '@utazzo/shared';
import { UzBadge, UzCard, UzSelect, UzSpinner } from '@utazzo/ui';
import { Activity, CheckCircle2, Clock, RefreshCw, XCircle } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { useSchedulerJobs, useSchedulerRuns } from '../use-scheduler';

const STATUS_TONE: Record<CronRunStatus, 'default' | 'secondary' | 'destructive' | 'warning'> = {
  RUNNING: 'secondary',
  SUCCESS: 'default',
  FAILED: 'destructive',
  SKIPPED: 'warning',
};

const STATUS_ICON: Record<CronRunStatus, React.ReactNode> = {
  RUNNING: <Activity className="h-3.5 w-3.5 text-blue-600" />,
  SUCCESS: <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />,
  FAILED: <XCircle className="h-3.5 w-3.5 text-red-600" />,
  SKIPPED: <Clock className="h-3.5 w-3.5 text-amber-600" />,
};

/// Radix Select rejects an empty-string item value (it reserves '' for
/// "clear the selection"). Use a sentinel for the "All ..." option and
/// map it back to undefined when building the API query.
const ALL_SENTINEL = '__all__';

const STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: ALL_SENTINEL, label: 'All statuses' },
  ...CRON_RUN_STATUSES.map((s) => ({ value: s, label: s.charAt(0) + s.slice(1).toLowerCase() })),
];

const PAGE_SIZE = 20;

/**
 * Admin-only view over the cron registry + the `cron_runs` audit log
 * (ADR-0037 follow-up). Read-only - manual triggers stay scripted so
 * the audit trail of who fired what stays intentional.
 *
 * Layout: a module-grouped grid of registered job cards at the top
 * (so leads cron jobs cluster together, customer jobs cluster
 * together, etc. as the registry grows), and a paginated history
 * table below filtered by job + status. A single Refresh button in
 * the page header re-fires BOTH the jobs query and the runs query
 * - polling is intentionally off (ADR-0037 follow-up §UI: cron
 * cadence is minutes / hours, not seconds, so an auto-refresh just
 * burned UI flickers without ever beating the operator's click).
 */
export const SchedulerLogsPage = (): React.JSX.Element => {
  const jobs = useSchedulerJobs();
  const [jobFilter, setJobFilter] = useState<string>(ALL_SENTINEL);
  const [statusFilter, setStatusFilter] = useState<string>(ALL_SENTINEL);
  const [page, setPage] = useState(1);
  const jobKeyParam = jobFilter === ALL_SENTINEL ? undefined : jobFilter;
  const statusParam = statusFilter === ALL_SENTINEL ? undefined : (statusFilter as CronRunStatus);
  const runs = useSchedulerRuns({
    page,
    pageSize: PAGE_SIZE,
    sortBy: 'startedAt',
    sortDir: 'desc',
    ...(jobKeyParam ? { jobKey: jobKeyParam } : {}),
    ...(statusParam ? { status: statusParam } : {}),
  });

  const jobOptions: ReadonlyArray<{ value: string; label: string }> = [
    { value: ALL_SENTINEL, label: 'All jobs' },
    ...(jobs.data?.jobs ?? []).map((j) => ({ value: j.jobKey, label: j.displayName })),
  ];

  // Module grouping. The `jobKey` is the canonical id and follows the
  // `<module>.<job>` convention (e.g. `leads.demote-stale-uncontacted`).
  // We derive the module from the prefix and bucket cards under it so
  // the page reads as "leads jobs ... customers jobs ..." instead of
  // a flat list that gets unreadable past 5+ cards. Falls back to
  // "Core" for any job key that doesn't carry a module separator.
  const groupedJobs = useMemo(() => {
    const list = jobs.data?.jobs ?? [];
    const byModule = new Map<string, SchedulerJobItem[]>();
    for (const job of list) {
      const moduleKey = deriveModuleKey(job.jobKey);
      if (!byModule.has(moduleKey)) byModule.set(moduleKey, []);
      byModule.get(moduleKey)!.push(job);
    }
    return Array.from(byModule.entries())
      .map(([moduleKey, modJobs]) => ({
        moduleKey,
        moduleLabel: humanizeModule(moduleKey),
        jobs: modJobs.slice().sort((a, b) => a.displayName.localeCompare(b.displayName)),
      }))
      .sort((a, b) => a.moduleLabel.localeCompare(b.moduleLabel));
  }, [jobs.data]);

  // One Refresh handler fires both queries. The button sits in the
  // page header (top right) so it covers both sections - the previous
  // arrangement only refetched runs, leaving the job cards stale.
  const refreshing = jobs.isFetching || runs.isFetching;
  const onRefresh = (): void => {
    void jobs.refetch();
    void runs.refetch();
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-zinc-900">Scheduler logs</h1>
          <p className="mt-1 max-w-3xl text-sm text-zinc-500">
            Every registered cron job, its current cadence, and the last few executions. Read-only -
            manual triggers stay scripted for now. Click Refresh to pull the latest snapshot.
          </p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </header>

      {/* Registered jobs - grouped by module so cards for the same
          feature cluster together as the registry grows. */}
      <section>
        <h2 className="mb-3 text-sm font-semibold text-zinc-700">Registered jobs</h2>
        {jobs.isLoading ? (
          <div className="flex items-center justify-center py-8">
            <UzSpinner />
          </div>
        ) : groupedJobs.length === 0 ? (
          <UzCard>
            <p className="py-4 text-center text-sm text-zinc-500">
              No cron jobs registered. Wire one via `SchedulerRegistry.register(this)` in a feature
              module's constructor.
            </p>
          </UzCard>
        ) : (
          <div className="space-y-5">
            {groupedJobs.map((group) => (
              <div key={group.moduleKey}>
                <div className="mb-2 flex items-baseline gap-2">
                  <h3 className="text-[11px] font-semibold tracking-wider text-zinc-600 uppercase">
                    {group.moduleLabel}
                  </h3>
                  <span className="text-[11px] text-zinc-400">
                    {group.jobs.length} {group.jobs.length === 1 ? 'job' : 'jobs'}
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  {group.jobs.map((j) => (
                    <JobCard key={j.jobKey} job={j} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Run history - heading on its own line, filters on a clearly
          separated toolbar row below, so the heading no longer fights
          the filters for horizontal space (was clipping to "...ecution
          history" on narrow widths). */}
      <section>
        <div className="mb-3">
          <h2 className="text-sm font-semibold text-zinc-700">Execution history</h2>
        </div>
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <div className="w-56 min-w-[180px] flex-1 sm:flex-none">
            <UzSelect
              name="jobFilter"
              label="Job"
              options={jobOptions}
              value={jobFilter}
              onValueChange={(v) => {
                setJobFilter(v ?? ALL_SENTINEL);
                setPage(1);
              }}
            />
          </div>
          <div className="w-44 min-w-[140px] flex-1 sm:flex-none">
            <UzSelect
              name="statusFilter"
              label="Status"
              options={STATUS_OPTIONS}
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v ?? ALL_SENTINEL);
                setPage(1);
              }}
            />
          </div>
        </div>

        <UzCard>
          {runs.isLoading ? (
            <div className="flex items-center justify-center py-10">
              <UzSpinner />
            </div>
          ) : runs.data?.data.length === 0 ? (
            <p className="py-10 text-center text-sm text-zinc-500">No runs match these filters.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-zinc-200 text-left text-[11px] font-medium tracking-wide text-zinc-500 uppercase">
                    <tr>
                      <th className="px-3 py-2">Job</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Triggered by</th>
                      <th className="px-3 py-2">Started</th>
                      <th className="px-3 py-2">Duration</th>
                      <th className="px-3 py-2">Result</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {runs.data?.data.map((r) => (
                      <RunRow key={r.id} run={r} />
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                page={page}
                totalPages={runs.data?.pagination.totalPages ?? 1}
                onChange={setPage}
              />
            </>
          )}
        </UzCard>
      </section>
    </div>
  );
};

/**
 * Derive a module key from a job key. Convention: `<module>.<job>`
 * (period separator), e.g. `leads.demote-stale-uncontacted` -> `leads`.
 * Falls back to `core` for any key without a separator so we never end
 * up with an empty group header.
 */
function deriveModuleKey(jobKey: string): string {
  const idx = jobKey.indexOf('.');
  if (idx <= 0) return 'core';
  return jobKey.slice(0, idx).toLowerCase();
}

/**
 * Human-readable module name. Splits on word separators (`-`, `_`) so
 * `lead_pipeline` reads as "Lead Pipeline" instead of "lead_pipeline".
 * Title-cases each segment.
 */
function humanizeModule(moduleKey: string): string {
  return moduleKey
    .split(/[-_]/)
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
}

const JobCard = ({ job }: { job: SchedulerJobItem }): React.JSX.Element => (
  <UzCard>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-zinc-900">{job.displayName}</span>
          {!job.enabled && (
            <UzBadge tone="secondary" pill>
              Disabled
            </UzBadge>
          )}
        </div>
        <code className="font-mono text-[11px] text-zinc-500">{job.jobKey}</code>
        {job.description && <p className="mt-1 text-xs text-zinc-600">{job.description}</p>}
      </div>
      {job.lastRun && (
        <UzBadge tone={STATUS_TONE[job.lastRun.status]} pill>
          <span className="flex items-center gap-1">
            {STATUS_ICON[job.lastRun.status]}
            {job.lastRun.status}
          </span>
        </UzBadge>
      )}
    </div>
    <div className="mt-3 grid grid-cols-1 gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
      <div>
        <span className="text-zinc-500">Expression</span>
        <div className="font-mono text-zinc-800">{job.expression}</div>
      </div>
      <div>
        <span className="text-zinc-500">Last run</span>
        <div className="text-zinc-800">
          {job.lastRun ? formatDateTime(job.lastRun.startedAt) : '-'}
        </div>
      </div>
      {job.lastRun?.errorMessage && (
        <div className="col-span-2 mt-1 rounded bg-red-50 px-2 py-1 text-[11px] text-red-700">
          {job.lastRun.errorMessage}
        </div>
      )}
    </div>
  </UzCard>
);

const RunRow = ({ run }: { run: SchedulerRunItem }): React.JSX.Element => (
  <tr className="hover:bg-zinc-50">
    <td className="px-3 py-2.5">
      <code className="font-mono text-[11px] text-zinc-700">{run.jobKey}</code>
    </td>
    <td className="px-3 py-2.5">
      <UzBadge tone={STATUS_TONE[run.status]} pill>
        <span className="flex items-center gap-1">
          {STATUS_ICON[run.status]}
          {run.status}
        </span>
      </UzBadge>
    </td>
    <td className="px-3 py-2.5 text-xs text-zinc-700">{run.triggeredBy}</td>
    <td className="px-3 py-2.5 text-xs text-zinc-700">{formatDateTime(run.startedAt)}</td>
    <td className="px-3 py-2.5 text-xs text-zinc-700">
      {run.durationMs !== null ? formatDuration(run.durationMs) : '-'}
    </td>
    <td className="px-3 py-2.5">
      {run.errorMessage ? (
        <span className="block max-w-md truncate text-xs text-red-700" title={run.errorMessage}>
          {run.errorMessage}
        </span>
      ) : run.summary ? (
        <code
          className="block max-w-md truncate font-mono text-[11px] text-zinc-600"
          title={JSON.stringify(run.summary)}
        >
          {JSON.stringify(run.summary)}
        </code>
      ) : (
        <span className="text-xs text-zinc-400">-</span>
      )}
    </td>
  </tr>
);

const Pagination = ({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
}): React.JSX.Element | null => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-end gap-2 border-t border-zinc-100 px-3 py-2 text-xs">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        className="rounded border border-zinc-200 px-2 py-1 text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
      >
        Prev
      </button>
      <span className="text-zinc-500">
        Page {page} of {totalPages}
      </span>
      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
        className="rounded border border-zinc-200 px-2 py-1 text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
      >
        Next
      </button>
    </div>
  );
};

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  return `${m}m ${s}s`;
}
